/**
 * useMonthlyRevenue Hook
 * Custom hook for fetching and saving monthly revenue using TanStack Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { revenueApi } from '../api/transactionApi';
import { useUIStore } from '../store/uiStore';

export const monthlyRevenueKeys = {
  all: ['monthlyRevenue'] as const,
  month: (year: number, month: number) => [...monthlyRevenueKeys.all, year, month] as const,
};

/**
 * Hook for fetching monthly revenue data
 */
export function useMonthlyRevenue(year: number, month: number) {
  const query = useQuery({
    queryKey: monthlyRevenueKeys.month(year, month),
    queryFn: () => revenueApi.getMonthlyRevenue(year, month),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  return {
    data: query.data?.data ?? null,
    currentRevenue: query.data?.data?.current?.amount ?? 0,
    rollingAverage: query.data?.data?.rollingAverage ?? 0,
    lastSixMonths: query.data?.data?.lastSixMonths ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for saving monthly revenue
 */
export function useSaveMonthlyRevenue() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ year, month, amount }: { year: number; month: number; amount: number }) =>
      revenueApi.saveMonthlyRevenue(year, month, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: monthlyRevenueKeys.all });
      addToast('success', 'Receita salva com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao salvar receita');
    },
  });
}
